
import React from 'react';
import { BookOpen, GraduationCap, ShieldCheck, Mail, MapPin } from 'lucide-react';
import { ClassLevel } from './types';

export const BRAND_NAME = "EduZone";
export const DOMAIN = "EduZone.pk";
export const CONTACT_EMAIL = "info@eduzone.pk";
export const WHATSAPP_NUMBER = "+92 329 4349972";
export const LOCATION = "Lahore, Pakistan";

const commonChapters = ["Chapter 1 (Sample)", "Chapter 2 (Sample)", "Chapter 3 (Sample)"];
const sampleDesc = "These sample notes are provided to help students understand the structure and quality of our study material. Our team is working tirelessly to compile the complete syllabus according to the latest All Board pattern.";

export const CLASSES: ClassLevel[] = [
  {
    id: "class-9",
    name: "Class 9",
    longIntro: "The 9th Grade is the foundational year for Matriculation in Pakistan. At EduZone, we understand that this is the first time students face external board examinations. Our notes are specifically curated to bridge the gap between textbook knowledge and board-specific requirements. Whether you are aiming for pre-medical, pre-engineering, or computer science streams, our Class 9 resources provide the perfect starting point for your academic journey under the All Board jurisdiction.",
    syllabusOverview: "The Class 9 syllabus for the All Board (All Board) covers 8 primary subjects. The board exams are typically divided into objective (MCQs) and subjective (Short and Long Questions) sections. Our notes follow the latest accelerated learning programs and standard patterns approved by the Punjab Curriculum and Textbook Board (PCTB).",
    subjects: [
      { id: "9-math", name: "Mathematics", description: sampleDesc, chapters: commonChapters },
      { id: "9-phys", name: "Physics", description: sampleDesc, chapters: commonChapters },
      { id: "9-chem", name: "Chemistry", description: sampleDesc, chapters: commonChapters },
      { id: "9-bio", name: "Biology", description: sampleDesc, chapters: commonChapters },
      { id: "9-eng", name: "English", description: sampleDesc, chapters: commonChapters },
      { id: "9-urdu", name: "Urdu", description: sampleDesc, chapters: commonChapters },
      { id: "9-isl", name: "Islamiat", description: sampleDesc, chapters: commonChapters },
      { id: "9-pak", name: "Pak Studies", description: sampleDesc, chapters: commonChapters }
    ],
    studyTips: [
      "Focus on the 'Exercise Questions' as they frequently appear in board papers.",
      "Practice diagrams for Biology and Physics regularly.",
      "Learn to manage time during the Objective (MCQ) portion.",
      "Review past papers from the last 5 years to understand the weightage of chapters."
    ],
    faqs: [
      { question: "When are the Class 9 Board Exams?", answer: "Usually, the exams are held in March/April every year." },
      { question: "Are these notes valid for other boards like Multan or Rawalpindi?", answer: "Yes, since all All Boards follow the same PCTB curriculum." }
    ]
  },
  {
    id: "class-10",
    name: "Class 10",
    longIntro: "Class 10 is the culminating year of Matriculation. The marks obtained here are critical for securing admission into prestigious colleges in Lahore and throughout Pakistan. EduZone provides strategic notes that focus on high-yield topics and the specific 'Paper Pattern' of the All Board. We prioritize clarity and conciseness to help students revise the entire syllabus effectively in the months leading up to the final board examinations.",
    syllabusOverview: "The Class 10 All Board syllabus focuses on advanced concepts across Science and Arts subjects. The examination structure is similar to Class 9, emphasizing critical thinking and conceptual clarity. Our resources ensure you are prepared for both the internal practical exams and the main theory papers.",
    subjects: [
      { id: "10-math", name: "Mathematics", description: sampleDesc, chapters: commonChapters },
      { id: "10-phys", name: "Physics", description: sampleDesc, chapters: commonChapters },
      { id: "10-chem", name: "Chemistry", description: sampleDesc, chapters: commonChapters },
      { id: "10-bio", name: "Biology", description: sampleDesc, chapters: commonChapters },
      { id: "10-eng", name: "English", description: sampleDesc, chapters: commonChapters },
      { id: "10-urdu", name: "Urdu", description: sampleDesc, chapters: commonChapters },
      { id: "10-isl", name: "Islamiat", description: sampleDesc, chapters: commonChapters },
      { id: "10-pak", name: "Pak Studies", description: sampleDesc, chapters: commonChapters }
    ],
    studyTips: [
      "Pay extra attention to Mathematics theorems as they carry significant marks.",
      "For English, practice your creative writing for essays and stories.",
      "Chemistry equations must be balanced and neat in your answers.",
      "Create a revision timetable that gives 2 hours daily to weak subjects."
    ],
    faqs: [
      { question: "Does EduZone provide practical notebooks?", answer: "We focus primarily on theory notes, but guidance for practicals will be added soon." },
      { question: "Is Class 10 harder than Class 9?", answer: "It requires more consistency, but the syllabus is manageable with our structured notes." }
    ]
  },
  {
    id: "class-11",
    name: "1st Year (HSSC-I)",
    longIntro: "Entering Intermediate (FSc/FA/ICS) is a significant jump in academic rigor. The 1st Year syllabus for the All Board is extensive and requires a deep conceptual understanding of subjects like Physics and Chemistry. EduZone acts as your academic partner, offering sample notes that simplify complex theories and provide a roadmap for achieving top positions in the All Boards.",
    syllabusOverview: "HSSC Part 1 syllabus is designed by the All Board to prepare students for professional university entrance tests like ECAT and MDCAT. The level of detail required in answers is much higher than Matriculation.",
    subjects: [
      { id: "11-comp", name: "Computer Science", description: sampleDesc, chapters: commonChapters },
      { id: "11-phys", name: "Physics", description: sampleDesc, chapters: commonChapters },
      { id: "11-math", name: "Mathematics", description: sampleDesc, chapters: commonChapters },
      { id: "11-eng", name: "English", description: sampleDesc, chapters: commonChapters },
      { id: "11-urdu", name: "Urdu", description: sampleDesc, chapters: commonChapters },
      { id: "11-isl", name: "Islamiat", description: sampleDesc, chapters: commonChapters }
    ],
    studyTips: [
      "Focus on conceptual clarity rather than rote learning.",
      "Prepare short questions from every nook and corner of the textbook.",
      "Numerical problems in Physics and Chemistry are the key to high scores.",
      "Understand the 'Pairing Scheme' issued by the board."
    ],
    faqs: [
      { question: "How to handle the pressure of FSc?", answer: "Consistent study of 3-4 hours daily from day one is the secret to success." }
    ]
  },
  {
    id: "class-12",
    name: "2nd Year (HSSC-II)",
    longIntro: "The final year of Intermediate is the most critical phase for any student in Pakistan. Your 2nd Year results determine your eligibility for medical and engineering universities. EduZone's mission is to provide premium quality notes that align with the rigorous standards of the All Board of Intermediate and Secondary Education (BISE Lahore). Our materials are designed to maximize your efficiency during the exam season.",
    syllabusOverview: "The 2nd Year syllabus builds upon the foundations of 1st Year. It includes advanced organic chemistry, calculus in mathematics, and modern physics. The board emphasizes both theoretical depth and analytical skills.",
    subjects: [
      { id: "12-comp", name: "Computer Science", description: sampleDesc, chapters: commonChapters },
      { id: "12-phys", name: "Physics", description: sampleDesc, chapters: commonChapters },
      { id: "12-math", name: "Mathematics", description: sampleDesc, chapters: commonChapters },
      { id: "12-eng", name: "English", description: sampleDesc, chapters: commonChapters },
      { id: "12-urdu", name: "Urdu", description: sampleDesc, chapters: commonChapters },
      { id: "12-pak", name: "Pak Studies", description: sampleDesc, chapters: commonChapters }
    ],
    studyTips: [
      "Give significant time to Pak Studies and Urdu, as they are often neglected.",
      "Practice organic chemistry mechanisms daily.",
      "Solve at least 10 previous board papers to master time management.",
      "Keep your presentation neat and use headings for all long answers."
    ],
    faqs: [
      { question: "Will full notes be free?", answer: "Yes, our mission is to provide high-quality education for free to all Pakistani students." }
    ]
  }
];
